import { BoldOutlined, CloseOutlined, ItalicOutlined, LinkOutlined } from '@ant-design/icons';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { BubbleMenu, EditorProvider, useCurrentEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useState } from 'react';

import { Button } from '../../Button/Button';
import { Modal } from '../../Modal/Modal';

import './RichTextEditor.scss';

const MenuBar = () => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState<string>('');

  const { editor } = useCurrentEditor();

  const openModal = useCallback(() => {
    if (!editor) {
      return;
    }
    setUrl(editor.getAttributes('link').href);
    setIsOpen(true);
  }, [editor]);

  const closeModal = useCallback(() => {
    if (!editor) {
      return;
    }
    setIsOpen(false);
    setUrl('');
  }, [editor]);

  const saveLink = useCallback(() => {
    if (!editor) {
      return;
    }
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    closeModal();
  }, [editor, url, closeModal]);

  const removeLink = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeModal();
  }, [editor, closeModal]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor-toolbar d-flex">
      <button
        type="button"
        onClick={() => {
          editor.chain().focus().toggleBold().run();
        }}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        <BoldOutlined />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        <ItalicOutlined />
      </button>
      <button type="button" onClick={openModal} className={editor.isActive('link') ? 'is-active' : ''}>
        <LinkOutlined />
      </button>

      <BubbleMenu
        className="bubble-menu-light"
        tippyOptions={{ duration: 150 }}
        editor={editor}
        shouldShow={({ editor, from, to }) => {
          // only show the bubble menu for links.
          return from === to && editor.isActive('link');
        }}
      >
        <div className="w-100 u-bg-white">
          <Button size="sm" variant="primary" type="button" onClick={openModal}>
            Edit
          </Button>
          <Button size="sm" variant="primary" type="button" onClick={removeLink}>
            Remove
          </Button>
        </div>
      </BubbleMenu>

      <Modal
        showModal={modalIsOpen}
        onClose={closeModal}
        header={
          <div className="w-100 d-flex align-items-center justify-content-between">
            <h2>Edit link</h2>
            <button type="button" onClick={closeModal} className="u-btn-reset">
              <CloseOutlined />
            </button>
          </div>
        }
      >
        <input className="w-100" value={url} onChange={(e) => setUrl(e.target.value)} />
        <div className="mt-3 d-flex gap-2">
          <Button size="sm" variant="secondary" type="button" onClick={removeLink}>
            Remove
          </Button>
          <Button size="sm" variant="primary" type="button" onClick={saveLink}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export const RichTextEditor = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder?: string;
  value?: string;
  onChange: (html: string) => void;
}) => {
  const EXTENSIONS = [
    StarterKit,
    Placeholder.configure({
      placeholder,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
    }),
  ];

  // Followup: text isnt clearing when value is reset
  return (
    <EditorProvider
      slotBefore={<MenuBar />}
      extensions={EXTENSIONS}
      content={value}
      onUpdate={(props) => {
        // eslint-disable-next-line react/prop-types
        const html = props.editor.getHTML();
        onChange(html);
      }}
    >
      <></>
    </EditorProvider>
  );
};
